import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Image, Form, Button, Dropdown, Grid, Loader, TextAreaProps, DropdownProps, DropdownItemProps, CheckboxProps, Dimmer } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import FileUploadMultiple from '../Common/SharedComponents/FileUploadMultiple';
import LIMSLogo from "../../assets/images/lims-logo.svg";
import axiosInstance from '../../helpers/axiosInstance';
import { utils } from '../../helpers/utils';
import { CaseWebFormModel, AttachmentChangeViewModel, IdNameViewModel } from '../../types/ServerDTO';

type FormErrorFields = {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  description: string;
  phoneNumber: string;
  captcha: string; // Added CAPTCHA validation
};

export const CaseWebForm: React.FC = () => {
  const { officeGuid } = useParams();

  const [formValues, setFormValues] = useState<CaseWebFormModel>({} as CaseWebFormModel);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [maxFileSize, setMaxFileSize] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    description: '',
    phoneNumber: '',
    captcha: '' // Added CAPTCHA error state
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get('/AdminSetting/GetMaxUploadFileSize').then(({ data, status }) => {
        if (status === 200) {
          setMaxFileSize(data);
        }
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
      await axiosInstance.get(`/form/${officeGuid}`).then(({ data, status }) => {
        if (status === 200) {
          let caseWebFormModel = data as CaseWebFormModel;
          caseWebFormModel.city = 'Minneapolis';
          caseWebFormModel.stateId = 33; // MN state ID
          caseWebFormModel.officeGUID = officeGuid;
          setFormValues(caseWebFormModel);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handlePhoneNumberInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const number = value.trim().replace(/[^0-9]/g, '');

    let formattedNumber = '';
    if (number.length < 4) {
      formattedNumber = number;
    } else if (number.length < 7) {
      formattedNumber = number.replace(/(\d{3})(\d{1})/, '$1-$2');
    } else if (number.length < 11) {
      formattedNumber = number.replace(/(\d{3})(\d{3})(\d{1})/, '$1-$2-$3');
    }
    setFormValues({ ...formValues, [name]: formattedNumber });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
    
    // Real-time validation for required fields when they become empty
    const requiredFields = ['firstName', 'lastName', 'email', 'subject'];
    if (requiredFields.includes(name)) {
      validate(false, name, value);
    }
  };

  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>, data: TextAreaProps) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
    
    // Real-time validation for required textarea when it becomes empty
    if (name === 'description') {
      validate(false, name, value);
    }
  };

  const handleDropdownChange = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleDropdownBlur = (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    const { name, value } = data;
    validate(false, name, value);
  };

  const handleRadioButtonChange = (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps, field: string) => {
    setFormValues({ ...formValues, [field]: data.value });
  };

  const handleAttachmentsChange = (updateFiles: AttachmentChangeViewModel, removeNewFile: Boolean = false) => {
    setFormValues((prevFiles) => {
      let updateAttachments = [...prevFiles.attachments?.updateAttachments] as AttachmentChangeViewModel[];
      if (removeNewFile) {
        updateAttachments = updateAttachments.filter((f) => f.attachmentGUID !== updateFiles.attachmentGUID);
      } else {
        updateAttachments.push({ ...updateFiles });
      }
      return { ...prevFiles, attachments: { ...prevFiles.attachments, updateAttachments: updateAttachments } }
    });
  }

  const handleCaptchaChange = (value: any) => {
    setFormValues({ ...formValues, captcha: value });
    // Clear captcha error when user completes it
    if (value && formErrors.captcha) {
      setFormErrors({ ...formErrors, captcha: '' });
    }
  }

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            firstName: !formValues?.firstName ? 'Please enter first name' : '',
            lastName: !formValues?.lastName ? 'Please enter last name' : '',
            email: !formValues?.email ? 'Please enter email' : (!utils.isValidEmail(formValues.email) ? 'Please provide a valid email' : ''),
            subject: !formValues?.subject ? 'Please enter subject' : '',
            description: !formValues?.description ? 'Please enter comment' : '',
            phoneNumber: formValues?.phoneNumber && formValues?.phoneNumber.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '',
            captcha: !formValues?.captcha ? 'Security verification is required' : '', // Added CAPTCHA validation
          };
        } else {
          switch (fieldName) {
            case 'firstName':
              errors.firstName = !value ? 'Please enter first name' : '';
              break;
            case 'lastName':
              errors.lastName = !value ? 'Please enter last name' : '';
              break;
            case 'email':
              errors.email = !value ? 'Please enter email' : (!utils.isValidEmail(value) ? 'Please provide a valid email' : '');
              break;
            case 'subject':
              errors.subject = !value ? 'Please enter subject' : '';
              break;
            case 'description':
              errors.description = !value ? 'Please enter comment' : '';
              break;
            case 'phoneNumber':
              errors.phoneNumber = value && value.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  // New function to validate and show only the first error for screen reader accessibility
  const validateFirstErrorOnly = async (): Promise<{ hasError: boolean; firstErrorField: string | null }> => {
    // Define the field validation order (same as form order)
    const validationOrder = [
      'firstName',
      'lastName', 
      'email',
      'subject',
      'description',
      'phoneNumber',
      'captcha'
    ];

    // Clear all errors first
    const clearedErrors: FormErrorFields = {
      firstName: '',
      lastName: '',
      email: '',
      subject: '',
      description: '',
      phoneNumber: '',
      captcha: ''
    };

    // Check each field in order and stop at the first error
    for (const fieldName of validationOrder) {
      let errorMessage = '';
      
      switch (fieldName) {
        case 'firstName':
          errorMessage = !formValues?.firstName ? 'Please enter first name' : '';
          break;
        case 'lastName':
          errorMessage = !formValues?.lastName ? 'Please enter last name' : '';
          break;
        case 'email':
          errorMessage = !formValues?.email ? 'Please enter email' : (!utils.isValidEmail(formValues.email) ? 'Please provide a valid email' : '');
          break;
        case 'subject':
          errorMessage = !formValues?.subject ? 'Please enter subject' : '';
          break;
        case 'description':
          errorMessage = !formValues?.description ? 'Please enter comment' : '';
          break;
        case 'phoneNumber':
          errorMessage = formValues?.phoneNumber && formValues?.phoneNumber.length < 12 ? 'Please enter a valid phone number which has at least 10 digits' : '';
          break;
        case 'captcha':
          errorMessage = !formValues?.captcha ? 'Security verification is required' : '';
          break;
      }

      // If we found an error, set only this error and return
      if (errorMessage) {
        clearedErrors[fieldName as keyof FormErrorFields] = errorMessage;
        setFormErrors(clearedErrors);
        return { hasError: true, firstErrorField: fieldName };
      }
    }

    // No errors found
    setFormErrors(clearedErrors);
    return { hasError: false, firstErrorField: null };
  }

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Use the new validation that only shows first error
    const { hasError, firstErrorField } = await validateFirstErrorOnly();
    
    if (!hasError) {
      try {
        let payload = { ...formValues };
        payload.masterData = null;
        payload.firstName = payload.firstName?.trim();
        payload.lastName = payload.lastName?.trim();
        payload.subject = payload.subject?.trim();
        
        await axiosInstance.post('/Case/SubmitWebForm', payload).then(({ data, status }) => {
          if (status === 200) {
            setReplyMessage(data);
            setIsFormSubmitted(true);
            // Focus on success message with slight delay
            setTimeout(() => {
              const successElement = document.getElementById('form-success-message');
              if (successElement) {
                successElement.focus();
              }
            }, 100);
            (document.getElementById('root') as any).scrollIntoView();
          }
        });
      } catch (error) {
        console.error('Submission error:', error);
        // Show user-friendly error message
        setFormErrors({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          description: 'There was an error submitting the form. Please try again or contact support.',
          phoneNumber: '',
          captcha: ''
        });
      }
    } else {
      // Focus on the first invalid field
      setTimeout(() => {
        if (firstErrorField) {
          const element = firstErrorField === 'captcha' 
            ? document.getElementById('recaptcha-container')?.querySelector('iframe')
            : document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
          if (element) {
            element.focus();
          }
        }
      }, 100);
    }
    setIsSubmitting(false);
  };

  // Add helper to check if form has errors
  const hasFormErrors = Object.values(formErrors).some((error) => error);
  const errorCount = Object.values(formErrors).filter(error => error).length;

  const convertToDropDownProps = (MasterItems: Array<IdNameViewModel>) => {
    return (MasterItems || []).map((item: IdNameViewModel) => ({
      key: item.id,
      text: item.name,
      value: item?.id,
    } as DropdownItemProps));
  }

  const { masterData, attachments } = formValues;
  const isInIframe = window.self !== window.top;

  return (
    <>
      <section className='section'>
        <div>
          {/* Live Region for Form Status */}
          <div 
            id="form-status" 
            aria-live="polite" 
            aria-atomic="true"
            className="sr-only"
          >
            {hasFormErrors ? 
              `Form has 1 error. Please review and correct the highlighted field.` : 
              (Object.keys(formValues).length > 0 ? 'Form is ready for submission' : '')
            }
          </div>

          <Dimmer.Dimmable as="div" dimmed={isLoading || isSubmitting}>
            <Dimmer active={isLoading || isSubmitting} inverted={true}>
              <Loader>
                {isSubmitting ? 'Submitting your form...' : 'Loading form data'}
              </Loader>
            </Dimmer>
            <Grid padded verticalAlign="middle">
              <Grid.Row>
                <Grid.Column mobile={16} tablet={8} computer={5}>
                  {!isInIframe && (
                    <>
                      <Image src={LIMSLogo} width={200} alt="LIMS Logo - Legal Information Management System" />
                      <h1>{masterData?.title || 'Contact Form'}</h1>
                    </>
                  )}
                  
                  {isFormSubmitted && (
                    <div 
                      id="form-success-message"
                      className="ui success message" 
                      role="alert" 
                      aria-live="polite"
                      tabIndex={-1}
                    >
                      <div className="content editor-content">
                        <div className="header">Success!</div>
                        <div dangerouslySetInnerHTML={{ __html: replyMessage.replace(/href/g, `target='_blank' href`) }} />
                      </div>
                    </div>
                  )}
                  
                  {!isFormSubmitted && (
                    <main id="main-form">
                      <Form className='form-custom' role="form" noValidate aria-labelledby="form-title">
                        <h2 id="form-title" className="sr-only">Contact Information Form</h2>
                        
                        {/* Required Fields Note - visually only, hidden from screen readers */}
                        <div aria-hidden="true" style={{ marginBottom: '1.75em', fontSize: '1.5 em'}}>
                          Fields marked with <span style={{ color: '#d32f2f', fontWeight: 'bold' }} aria-hidden="true">*</span> are required.
                        </div>

                        <Form.Field>
                          <label htmlFor="firstName" style={{ display: 'block', marginBottom: '0.25rem' }}>
                            First name
                            <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: '0.25rem' }} aria-hidden="true">*</span>
                          </label>
                          <Form.Input
                            id="firstName"
                            name="firstName"
                            value={formValues.firstName || ''}
                            onChange={handleInputChange}
                            error={!!formErrors?.firstName}
                            required
                            aria-required="true"
                            aria-describedby="firstName-error"
                            aria-invalid={!!formErrors?.firstName}
                            autoComplete="given-name"
                          />
                          {formErrors?.firstName && (
                            <div 
                              id="firstName-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.firstName}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>
                          <label htmlFor="lastName" style={{ display: 'block', marginBottom: '0.25rem' }}>
                            Last name
                            <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: '0.25rem' }} aria-hidden="true">*</span>
                          </label>
                          <Form.Input
                            id="lastName"
                            name="lastName"
                            value={formValues.lastName || ''}
                            onChange={handleInputChange}
                            error={!!formErrors?.lastName}
                            required
                            aria-required="true"
                            aria-describedby="lastName-error"
                            aria-invalid={!!formErrors?.lastName}
                            autoComplete="family-name"
                          />
                          {formErrors?.lastName && (
                            <div 
                              id="lastName-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.lastName}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>
                          <Form.Input
                            id="pronouns"
                            label="Pronouns"
                            name="pronouns"
                            for="pronouns"
                            value={formValues.pronouns || ''}
                            onChange={handleInputChange}
                            maxLength={30}
                            aria-describedby="pronouns-help pronouns-count"
                            placeholder="e.g., she/her, he/him, they/them"
                          />
                          <div id="pronouns-help" style={{ color: '#595959', fontSize: '1.5 em', marginTop: '-1.4em', marginBottom: '1.75em' }}>
                            Optional. Examples: she/her, he/him, they/them (maximum 30 characters)
                          </div>
                          {formValues.pronouns && (
                            <div
                              id="pronouns-count"
                              className="character-count"
                              style={{
                                color: formValues.pronouns.length > 22 ? '#b71c1c' : '#424242',
                                fontSize: '1.2rem',
                                marginTop: '0.2rem',
                                fontWeight: formValues.pronouns.length > 22 ? 'bold' : 'normal'
                              }}
                              aria-live="polite"
                              role="status"
                            >
                              {formValues.pronouns.length > 22 && (
                                <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              )}
                              {30 - formValues.pronouns.length} characters remaining
                              {formValues.pronouns.length > 22 && (
                                <span> - Character limit warning!</span>
                              )}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>
                          <label htmlFor="email" style={{ display: 'block', marginBottom: '0.25rem' }}>
                            Email
                            <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: '0.25rem' }} aria-hidden="true">*</span>
                          </label>
                          <Form.Input
                            id="email"
                            name="email"
                            type="email"
                            value={formValues.email || ''}
                            onChange={handleInputChange}
                            error={!!formErrors?.email}
                            required
                            aria-required="true"
                            aria-describedby="email-error"
                            aria-invalid={!!formErrors?.email}
                            autoComplete="email"
                          />
                          {formErrors?.email && (
                            <div 
                              id="email-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.email}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>
                          <Form.Input
                            id="phoneNumber"
                            label="Phone number"
                            name="phoneNumber"
                            for="phoneNumber"
                            type="tel"
                            value={formValues.phoneNumber || ''}
                            onChange={handlePhoneNumberInputChange}
                            maxLength={12}
                            aria-describedby="phoneNumber-help phoneNumber-error"
                            aria-invalid={!!formErrors?.phoneNumber}
                            autoComplete="tel"
                          />
                          <div id="phoneNumber-help" style={{  color: '#595959', fontSize: '1.5 em', marginTop: '-1.4em', marginBottom: '1.75em' }}>
                            Optional. Must be at least 10 digits if provided.
                          </div>
                          {formErrors?.phoneNumber && (
                            <div 
                              id="phoneNumber-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.phoneNumber}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>
                            <fieldset
                                style={{ border: 'none', padding: 0, margin: 0 }}
                                aria-labelledby="phone-type-legend"
                                aria-describedby="phone-type-help"
                                role="radiogroup"
                            >
                                <legend id="phone-type-legend" style={{ fontSize: '1.15em', color: 'rgba(0,0,0,.87)', marginTop: '0.3rem', fontWeight: 500 }}>
                                    Phone type
                                </legend>
                                <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.3rem' }}>
                                    {masterData?.phoneType?.map((phoneType, index) => (
                                        <Form.Radio
                                            key={phoneType.id}
                                            id={`phoneType-${phoneType.id}`}
                                            label={phoneType.name}
                                            name="phoneTypeId"
                                            for={`phoneType-${phoneType.id}`}
                                            value={phoneType.id}
                                            checked={formValues.phoneTypeId === phoneType.id}
                                            onChange={(e, data) => handleRadioButtonChange(e, data, 'phoneTypeId')}
                                            style={{
                                                marginBottom: '0.5rem',
                                                fontSize: '1.4rem'
                                            }}
                                        />
                                    ))}
                                </div>
                                <div id="phone-type-help" style={{ color: '#595959', fontSize: '1.5 em', marginTop: '-1.4em', marginBottom:'0.4rem', scrollMarginBottom:'2.0em' }}>
                                    Optional. Select the type of phone number you provided.
                                </div>
                            </fieldset>
                        </Form.Field>

                        <Form.Field>
                          <Form.Input
                            id="address"
                            label="Address"
                            name="address"
                            for="address"
                            value={formValues.address || ''}
                            onChange={handleInputChange}
                            aria-describedby="address-help"
                            autoComplete="street-address"
                          />
                        </Form.Field>

                        <Form.Field>
                          <Form.Input
                            id="city"
                            label="City"
                            name="city"
                            for="city"
                            value={formValues.city || ''}
                            onChange={handleInputChange}
                            aria-describedby="city-help"
                            autoComplete="address-level2"
                          />
                        </Form.Field>

                        <Form.Field>
                          <div className='field'>
                            <label htmlFor="stateId">State</label>
                            <Dropdown
                              id="stateId"
                              name="stateId"
                              for="stateId"
                              options={convertToDropDownProps(masterData?.state || [])}
                              fluid
                              selection
                              clearable
                              value={formValues.stateId ? formValues.stateId : ''}
                              onChange={handleDropdownChange}
                              placeholder="Choose a state..."
                              aria-label="State selection"
                              aria-describedby="state-help"
                              role="combobox"
                              aria-haspopup="listbox"
                            />
                          </div>
                        </Form.Field>

                        <Form.Field>
                          <Form.Input
                            id="zipCode"
                            label="Zip"
                            name="zipCode"
                            for="zipCode"
                            value={formValues.zipCode || ''}
                            onChange={handleInputChange}
                            aria-describedby="zip-help"
                            autoComplete="postal-code"
                          />
                        </Form.Field>

                        <Form.Field>
                          <label htmlFor="subject" style={{ display: 'block', marginBottom: '0.25rem' }}>
                            Subject
                            <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: '0.25rem' }} aria-hidden="true">*</span>
                          </label>
                          <Form.Input
                            id="subject"
                            name="subject"
                            value={formValues.subject || ''}
                            onChange={handleInputChange}
                            error={!!formErrors?.subject}
                            required
                            aria-required="true"
                            maxLength={255}
                            aria-describedby="subject-count subject-error"
                            aria-invalid={!!formErrors?.subject}
                          />
                          {formValues.subject?.length === 255 && (
                            <div 
                              id="subject-limit"
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              Maximum character limit exceeded
                            </div>
                          )}
                          {formValues.subject && formValues.subject.length < 255 && (
                            <div id="subject-count" style={{ fontSize: '1.1rem', color: '#666', marginTop: '0.3rem' }}>
                              {255 - formValues.subject.length} characters remaining
                            </div>
                          )}
                          {formErrors?.subject && (
                            <div 
                              id="subject-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.subject}
                            </div>
                          )}
                        </Form.Field>

                        <Form.Field>                        
                          <label htmlFor="description" style={{ display: 'block', marginBottom: '0.25rem' }}>
                                                      Comment
                            <span style={{ color: '#d32f2f', fontWeight: 'bold', marginLeft: '0.25rem' }} aria-hidden="true">*</span>
                          </label>
                          <Form.TextArea
                            id="description"
                            required
                            name="description"
                            style={{ minHeight: 100 }}
                            value={formValues.description ? formValues.description : ''}
                            onChange={handleTextAreaChange}
                            aria-required="true"
                            aria-describedby="description-error"
                            aria-invalid={!!formErrors?.description}
                          />
                          {formErrors?.description && (
                            <div 
                              id="description-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.description}
                            </div>
                          )}
                        </Form.Field>

                        {formValues.attachments &&
                          <Grid>
                            <Grid.Column>
                              <div className={`field ${attachments?.isRequired ? 'required' : null}`}>
                                <label htmlFor="file-upload">
                                  Attach a file
                                  {attachments?.isRequired && <span aria-hidden="true" style={{ color: '#d32f2f', fontWeight: 'bold' }}> *</span>}
                                </label>
                                <div style={{ color: '#595959', fontSize: '1.5 em', marginTop: '0.3rem', marginBottom: '0.5rem' }}>
                                  Optional. You may attach relevant files to support your message.
                                </div>
                                <FileUploadMultiple
                                  caseId={0}
                                  uniqueKey="case_attachments"
                                  apiUrl={`/Upload/UploadWebFormAttachment`}
                                  title={attachments?.documentLabel}
                                  single={false}
                                  readOnly={false}
                                  handleUpdateDocument={handleAttachmentsChange}
                                  sectionClassName=''
                                  maxFileSize={maxFileSize}
                                />
                              </div>
                            </Grid.Column>
                          </Grid>
                        }

                        <Grid>
                          <Grid.Column>
                            <div 
                              role="note" 
                              style={{ 
                                padding: '1rem', 
                                backgroundColor: '#f8f9fa', 
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                margin: '1rem 0'
                              }}
                            >
                              <strong>Privacy Notice:</strong> Information you provide is subject to the Minnesota Government Data Practices Act. This law classifies certain information as available to the public on request.
                            </div>
                          </Grid.Column>
                        </Grid>

                        <div style={{ margin: '15px 0' }}>
                          <label 
                            htmlFor="recaptcha-container" 
                            style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}
                          >
                            Security Verification <span aria-hidden="true" style={{ color: '#d32f2f', fontWeight: 'bold' }}>*</span>
                          </label>
                          <div style={{ color: '#595959', fontSize: '1.5 em', marginBottom: '0.5rem' }}>
                            Please complete this security check to verify you are human.
                          </div>
                          <div id="recaptcha-container">
                            <ReCAPTCHA
                              sitekey={`${process.env.REACT_APP_GOOGLE_MAP_KEY}`}
                              onChange={handleCaptchaChange}
                              aria-label="Security verification - complete the challenge to prove you are human"
                              aria-describedby="captcha-error"
                            />
                          </div>
                          {formErrors?.captcha && (
                            <div 
                              id="captcha-error" 
                              className="error-message" 
                              style={{ color: '#d32f2f', fontWeight: 'bold', display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
                              role="alert"
                              aria-live="polite"
                            >
                              <span style={{ marginRight: '0.5rem' }} aria-hidden="true">⚠️</span>
                              {formErrors?.captcha}
                            </div>
                          )}
                          <div style={{ color: '#595959', fontSize: '1.5 em', marginTop: '0.5rem' }}>
                            <strong>Contact form - Having trouble?</strong> If you cannot complete the security verification, please <a href="mailto:AdjureSupport@minneapolismn.gov" style={{ color: '#2d76d5' }}>contact us</a> for assistance.
                          </div>
                        </div>

                        <Grid>
                          <Grid.Column>
                            <Button 
                              type="submit" 
                              onClick={handleSubmit} 
                              disabled={isSubmitting}
                              className='right floated button' 
                              style={{ 
                                padding: '1.1rem 2.1rem', 
                                borderRadius: '0rem', 
                                fontSize: '1.8rem',
                                minHeight: '44px',
                                minWidth: '44px',
                                opacity: isSubmitting ? 0.6 : 1
                              }}
                              aria-describedby="submit-status"
                            >
                              {isSubmitting ? 'Submitting...' : 'Submit'}
                            </Button>
                            <div id="submit-status" className="sr-only">
                              {isSubmitting ? 'Form is being submitted, please wait' : 'Submit the form after completing all required fields'}
                            </div>
                          </Grid.Column>
                        </Grid>
                      </Form>
                    </main>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Dimmer.Dimmable>
        </div>
        <br />
      </section>
      
          <style>{`
        .ui.form .field {margin-bottom: 1.75em}

        .sr-only {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }

        .sr-only-focusable:focus {
          position: static !important;
          width: auto !important;
          height: auto !important;
          padding: inherit !important;
          margin: inherit !important;
          overflow: visible !important;
          clip: auto !important;
          white-space: normal !important;
        }
      `}</style>
    </>
  );
}

export default CaseWebForm;